import React, { useState, useEffect, useRef } from "react";
import { X, Search, ChevronLeft, ChevronRight, FileText, CheckCircle, XCircle, RefreshCw, ArrowDownCircle, BanknoteIcon } from "lucide-react";
import { fetchPayments, updateBankPayment } from "../../../api/paymentApi";
import PaymentCardSkeleton from "./loading/PaymentCardSkeleton";
import { toast } from "react-toastify";
import CourseList from "../../../component/CourseList";
import LoadingFloter from "../../../component/LoadingFloter";
import DownloadExcelButton from "../../dashboard/DownloadExcelButton";

const PaymentCards = () => {
  const [payments, setPayments] = useState([]);
  const [courseId, setCourseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(12);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [adminMsg, setAdminMsg] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isIgnoring, setIsIgnoring] = useState(false);
  const [isMarkingAsFree, setIsMarkingAsFree] = useState(false);
  
  // Create refs for the modal contents
  const modalContentRef = useRef(null);
  const ignoreModalContentRef = useRef(null);

  useEffect(() => {
    const getPayments = async () => {
      try {
        setUpdating(true);
        if(!courseId) {
        };
        const data = await fetchPayments(currentPage, limit, courseId, searchQuery);
        setPayments(data.paymentsData);
        setTotalCount(data.count);

        data.paymentsData.forEach(payment => {
        if (payment.slip) {
          const img = new Image();
          img.src = `https://${payment.slip}`;
        }
      });
      
      } catch (err) {
        setError("Failed to load payment data. Please try again.");
      }finally{
        setUpdating(false);
        setLoading(false);
      }
    };

    getPayments();
  }, [courseId, searchQuery, currentPage, limit, refreshFlag]);

  // Add event listener for clicks outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only check slip modal if ignore modal is not open
      if (
        !isIgnoreModalOpen &&
        isModalOpen &&
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target)
      ) {
        closeModal();
      }

      // Always check ignore modal when it's open
      if (
        isIgnoreModalOpen &&
        ignoreModalContentRef.current &&
        !ignoreModalContentRef.current.contains(event.target)
      ) {
        closeIgnoreModal();
      }
    };

    // Add event listener when either modal is open
    if (isModalOpen || isIgnoreModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen, isIgnoreModalOpen]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,     // set to false for 24-hour format
  });
};

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage * limit < totalCount) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    setSearchQuery(searchTerm.trim());
  };

  const handleSlipView = (id) => {
    setIsModalOpen(true);
    setSelectedSlip(payments.find((payment) => payment.purchased_id === id));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSlip(null);
  };

  const closeIgnoreModal = () => {
    setIsIgnoreModalOpen(false);
    setAdminMsg("");
    // We don't close the slip modal here
  };

  const handleUpdateStatusClick = async (status, action = "verified") => {
    try {
      const email = selectedSlip.email;
      const paymentId = selectedSlip.purchased_id;
      
      if (status === 1) {
        setIsVerifying(true);
      } else if (status === 10) {
        setIsMarkingAsFree(true);
      }
      
      await updateBankPayment({ email, status, paymentId, adminMsg });
      toast.success(`Payment ${action} successfully!`);
      setRefreshFlag((prev) => !prev);
      closeModal();
    } catch (err) {
      toast.error("Payment update failed!");
    } finally {
      setIsVerifying(false);
      setIsMarkingAsFree(false);
    }
  };

  const handleIgnoreClick = () => {
    setIsIgnoreModalOpen(true);
    // We don't close the slip modal here
  };

  const handleIgnoreSubmit = async () => {
    if (!adminMsg.trim()) {
      toast.error("Please provide a reason for ignoring this payment");
      return;
    }

    setIsIgnoring(true);
    try {
      const email = selectedSlip.email;
      const paymentId = selectedSlip.purchased_id;
      // Using status 5 for ignored payments
      await updateBankPayment({ email, status: 5, paymentId, adminMsg });
      toast.success("Payment ignored successfully!");
      setRefreshFlag((prev) => !prev);
      closeIgnoreModal();
      closeModal(); // Only close both modals after successful submission
    } catch (err) {
      toast.error("Failed to ignore payment!");
    } finally {
      setIsIgnoring(false);
    }
  };

  const renderStatusBadge = (status) => {
    if (status === 1) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle size={12} className="mr-1" />
          Verified
        </span>
      );
    } else if (status === 5) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle size={12} className="mr-1" />
          Ignored
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw size={12} className="mr-1" />
          Pending
        </span>
      );
    }
  };

  if (loading) {
    return (
    <div className="p-1 sm:p-6">
      <PaymentCardSkeleton count={6} />
    </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl font-semibold text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto  p-4 md:p-6">
      {
        updating &&
        <LoadingFloter/>
      }
      <div className="mb-8  mt-6 md:mt-0">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex flex-row items-center gap-2"><BanknoteIcon className="size-7"/> Payment Management</h1>
        <p className="text-gray-600 mt-1">
          Review and process student payment records
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 gap-4 items-end">
          <div className="w-full md:w-1/3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Course
            </label>
            <CourseList
              courseId={courseId}
              setCourseId={setCourseId}
              showLabel={false}
              assignAfterRender = {false}
            />
          </div>
          <div className="w-full md:w-2/3 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Payments
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1 rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by order ID, name, email or phone..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit();
                    }
                  }}
                  value={searchTerm}
                />
              </div>
              <button
                type="button"
                onClick={handleSearchSubmit}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500"
              >
                <Search size={16} className="mr-2 hidden sm-block" />
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{payments.length}</span> of{" "}
            <span className="font-medium">{totalCount}</span> payments
          </div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="inline-flex justify-center items-center w-12 h-12 rounded-full bg-gray-100 mb-4">
            <FileText size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No payments found</h3>
          <p className="mt-2 text-gray-500 max-w-md mx-auto">
            No payment records match your current search criteria. Try adjusting your filters or search terms.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {payments.map((payment) => (
              <div
                key={payment.purchased_id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
              >
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-medium">Order #{payment.order_id}</h2>
                    {renderStatusBadge(payment.status)}
                  </div>
                </div>
                
                <div className="p-5">
                  {/* Course information */}
                  <div className="mb-4 border-b border-gray-100 pb-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{payment.cTitle}</h3>
                    <p className="text-sm text-gray-800">
                      Quota: {payment.title}
                    </p>
                  </div>
                  
                  {/* Payment details grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Student</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {payment.first_name} {payment.last_name}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Admission</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {payment.user_id}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Payment Date</p>
                      <p className="text-sm text-gray-900 mt-1">{formatDate(payment.date)}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Payment Time</p>
                      <p className="text-sm text-gray-900 mt-1">{formatTime(payment.date)}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Amount</p>
                      <p className="text-base font-semibold text-indigo-600 mt-1">
                        Rs. {parseFloat(payment.amount).toLocaleString()}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Bank</p>
                      <p className="text-sm text-gray-900 mt-1 flex items-center">
                        {payment.isBankPayment === 1 ? (
                          <span className="inline-flex items-center">
                            <ArrowDownCircle size={14} className="mr-1 text-blue-600" />
                            {payment.bank || "Bank Transfer"}
                          </span>
                        ) : (
                          "Online Payment"
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Contact details */}
                  <div className="mb-4 bg-gray-50 rounded p-3 text-sm">
                    <div className="flex items-center mb-1">
                      <p className="text-gray-600">{payment.phone_number}</p>
                    </div>
                    <div className="flex items-center">
                      <p className="text-gray-600 truncate w-full">{payment.email}</p>
                    </div>
                  </div>

                  {/* Action footer */}
                  <div className="mt-2 flex justify-end">
                    {payment.slip && (
                      <button
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500"
                        onClick={() => handleSlipView(payment.purchased_id)}
                        aria-label="View payment slip"
                      >
                        <FileText size={14} className="mr-1.5" />
                        View Slip
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {Math.ceil(totalCount / limit)}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500 ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage * limit >= totalCount}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500 ${
                  currentPage * limit >= totalCount
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
                aria-label="Next page"
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Slip View Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div
            ref={modalContentRef}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
          >
            <div className="flex justify-between items-center p-5 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Payment Verification</h3>
              <button
                onClick={closeModal}
                className="rounded-full p-1 hover:bg-gray-100 text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label="Close slip modal"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {selectedSlip && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left side - Slip Image */}
                  <div className="flex flex-col">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Slip</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center h-96">
                      <img
                        src={`https://${selectedSlip.slip}`}
                        alt="Payment Slip"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/api/placeholder/600/800";
                          e.target.classList.add("border", "border-red-300");
                        }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500 text-center">
                      Uploaded on {formatDate(selectedSlip.date)}
                    </p>
                  </div>
                  
                  {/* Right side - Payment Details */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <dl className="space-y-3 text-sm">
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Order ID</dt>
                          <dd className="col-span-2 text-gray-900">#{selectedSlip.order_id}</dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Student</dt>
                          <dd className="col-span-2 text-gray-900">
                            {selectedSlip.first_name} {selectedSlip.last_name}
                          </dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Course</dt>
                          <dd className="col-span-2 text-gray-900">{selectedSlip.cTitle}</dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Quota</dt>
                          <dd className="col-span-2 text-gray-900">{selectedSlip.title}</dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Amount</dt>
                          <dd className="col-span-2 font-semibold text-gray-900">
                            Rs. {parseFloat(selectedSlip.amount).toLocaleString()}
                          </dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Bank</dt>
                          <dd className="col-span-2 text-gray-900">
                            {selectedSlip.isBankPayment === 1 ? selectedSlip.bank || "Bank Transfer" : "Online Payment"}
                          </dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Contact</dt>
                          <dd className="col-span-2 text-gray-900">
                            <div>{selectedSlip.phone_number}</div>
                            <div className="truncate">{selectedSlip.email}</div>
                          </dd>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <dt className="font-medium text-gray-500">Status</dt>
                          <dd className="col-span-2">{renderStatusBadge(selectedSlip.status)}</dd>
                        </div>
                      </dl>
                    </div>

                    {/* Actions for pending payments */}
                    {selectedSlip.status !== 1 && selectedSlip.status !== 5 && (
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                        <h5 className="font-medium text-indigo-900 mb-2">Payment Actions</h5>
                        <p className="text-sm text-indigo-700 mb-4">
                          Please verify this payment or mark it as ignored if invalid.
                        </p>
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => handleUpdateStatusClick(1)}
                            disabled={isVerifying}
                            className={`inline-flex justify-center items-center px-4 py-2 bg-green-600 text-white rounded-md font-medium shadow-sm hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                              isVerifying ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          >
                            {isVerifying ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Verifying Payment
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <CheckCircle size={16} className="mr-2" />
                                Verify Payment
                              </span>
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleUpdateStatusClick(10)}
                            disabled={isMarkingAsFree}
                            className={`inline-flex justify-center items-center px-4 py-2 bg-purple-600 text-white rounded-md font-medium shadow-sm hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                              isMarkingAsFree ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          >
                            {isMarkingAsFree ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <ArrowDownCircle size={16} className="mr-2" />
                                Mark as Free
                              </span>
                            )}
                          </button>
                          
                          <button
                            onClick={handleIgnoreClick}
                            disabled={isVerifying || isMarkingAsFree}
                            className={`inline-flex justify-center items-center px-4 py-2 bg-red-600 text-white rounded-md font-medium shadow-sm hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                              (isVerifying || isMarkingAsFree) ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          >
                            <XCircle size={16} className="mr-2" />
                            Ignore Payment
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Reset action for verified/ignored payments */}
                    {selectedSlip && (selectedSlip.status === 1 || selectedSlip.status === 5) && (
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                        <h5 className="font-medium text-amber-900 mb-2">Reset Status</h5>
                        <p className="text-sm text-amber-700 mb-4">
                          This payment has already been {selectedSlip.status === 1 ? "verified" : "ignored"}.
                          You can reset it to pending status if needed.
                        </p>
                        <button
                          onClick={() => handleUpdateStatusClick(0, "reset")}
                          disabled={isVerifying}
                          className={`inline-flex justify-center items-center w-full px-4 py-2 bg-amber-500 text-white rounded-md font-medium shadow-sm hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                            isVerifying ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          {isVerifying ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <RefreshCw size={16} className="mr-2" />
                              Reset to Pending
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-5 border-t bg-gray-50 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ignore Payment Modal */}
      {isIgnoreModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div
            ref={ignoreModalContentRef}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="flex justify-between items-center p-5 border-b">
              <div className="flex items-center">
                <XCircle size={20} className="text-red-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Ignore Payment</h3>
              </div>
              <button
                onClick={closeIgnoreModal}
                className="rounded-full p-1 hover:bg-gray-100 text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                aria-label="Close ignore modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-4">
                Please provide a reason for ignoring this payment. This message
                will be sent to the student.
              </p>
              <div className="mb-1">
                <label htmlFor="ignore-reason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for ignoring
                </label>
                <textarea
                  id="ignore-reason"
                  value={adminMsg}
                  onChange={(e) => setAdminMsg(e.target.value)}
                  placeholder="Enter reason for ignoring payment..."
                  className="w-full border border-gray-300 rounded-md p-3 h-32 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm placeholder-gray-400"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 italic">
                Note: This message will be visible to the student and cannot be changed afterward.
              </p>
            </div>
            <div className="p-5 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={closeIgnoreModal}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-0"
              >
                Cancel
              </button>
              <button
                onClick={handleIgnoreSubmit}
                disabled={isIgnoring || !adminMsg.trim()}
                className={`px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-0 ${
                  isIgnoring || !adminMsg.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isIgnoring ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing
                  </span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Excel Section */}
      <div className="mt-12 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Payment Data</h2>
          <DownloadExcelButton />
        </div>
      </div>
    </div>
  );
};

export default PaymentCards;